import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, Col, Row, Card, Form, Button } from '@themesberg/react-bootstrap';
import axios from "axios";
import { API_URL, END_POINT_INDUSTRIES } from "../const";
import { useNavigate, Link } from 'react-router-dom';
import { showSuccessToast } from "../utils/toast";
import { FormikSearchableSelect, buildSelectOptions } from '../components/SearchableSelect';
import CompanyCheckboxPicker from '../components/CompanyCheckboxPicker';

export const IndustryForm = ({ onSuccess, onCancel, onSubmittingChange, menuPortalTarget }) => {
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');
  const [industries, setIndustries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allowMultipleCompanies, setAllowMultipleCompanies] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [optionsError, setOptionsError] = useState("");
  const [optionsReloadKey, setOptionsReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoadingOptions(true);
    setOptionsError("");
    const config = { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API_URL}auth/context`, config),
      axios.get(API_URL + END_POINT_INDUSTRIES, config),
    ]).then(([contextResponse, industryResponse]) => {
      if (!active) return;
      const context = contextResponse.data;
      const industryList = industryResponse.data;
      if (!context.status || !Array.isArray(context.data?.companies) ||
          !industryList.status || !Array.isArray(industryList.data)) {
        throw new Error('Invalid industry or company information');
      }
      setCompanies(context.data.companies);
      setAllowMultipleCompanies(Boolean(context.data.is_super_admin || context.data.allow_multiple_companies));
      setIndustries(industryList.data);
    }).catch(() => {
      if (active) setOptionsError('Unable to load industry and company options. Please try again.');
    }).finally(() => {
      if (active) setLoadingOptions(false);
    });
    return () => { active = false; };
  }, [token, optionsReloadKey]);

  const industryOptions = useMemo(() => [
    { value: '0', label: 'No parent (top-level industry)' },
    ...buildSelectOptions(industries),
  ], [industries]);

  const formik = useFormik({
    initialValues: { name: "", parent_id: "0", company_ids: [] },
    validationSchema: Yup.object({
      name: Yup.string().required("Industry Name is required"),
      company_ids: allowMultipleCompanies ? Yup.array().min(1, "Select at least one company") : Yup.array(),
    }),
    onSubmit: async (values, { setSubmitting, setStatus, setErrors }) => {
      if (loadingOptions || optionsError) {
        setSubmitting(false);
        return;
      }
      setStatus(null);
      onSubmittingChange?.(true);
      let result;
      try {
        const response = await axios.post(API_URL + END_POINT_INDUSTRIES, values, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!response.data.status) {
          setStatus(response.data.message || 'Failed to create industry.');
          return;
        }
        result = response.data;
      } catch (error) {
        const errors = error.response?.data?.errors;
        if (errors) {
          setErrors(Object.fromEntries(
            Object.entries(errors).map(([field, messages]) => [
              field, Array.isArray(messages) ? messages[0] : messages,
            ])
          ));
        }
        setStatus(error.response?.data?.message || 'Failed to create industry.');
      } finally {
        setSubmitting(false);
        onSubmittingChange?.(false);
      }
      if (result) onSuccess(result);
    },
  });

  const fieldsDisabled = formik.isSubmitting || loadingOptions || Boolean(optionsError);

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting || loadingOptions}>
      {loadingOptions && <div className="text-muted mb-3" role="status">Loading industry and company options...</div>}
      {optionsError && (
        <Alert variant="danger">
          {optionsError}
          <Button variant="link" type="button" onClick={() => setOptionsReloadKey(value => value + 1)}>
            Retry
          </Button>
        </Alert>
      )}
      {formik.status && <Alert variant="danger">{formik.status}</Alert>}
      <fieldset disabled={fieldsDisabled}>
        <Row>
          <Col sm={6} className="mb-3">
            <Form.Group controlId="industry-parent">
              <Form.Label>Parent Industry (optional)</Form.Label>
              <FormikSearchableSelect
                formik={formik}
                name="parent_id"
                inputId="industry-parent"
                options={industryOptions}
                placeholder="Search parent industry..."
                isDisabled={fieldsDisabled}
                menuPortalTarget={menuPortalTarget}
              />
            </Form.Group>
          </Col>
          <Col md={6} className="mb-3">
            <Form.Group controlId="industry-name">
              <Form.Label>Industry Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                {...formik.getFieldProps("name")}
                isInvalid={formik.touched.name && formik.errors.name}
              />
              <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          {(allowMultipleCompanies || companies.length > 1) && (
            <Col md={12} className="mb-3">
              <CompanyCheckboxPicker
                companies={companies}
                selectedIds={formik.values.company_ids}
                onChange={(companyIds) => formik.setFieldValue('company_ids', companyIds)}
                error={formik.touched.company_ids && formik.errors.company_ids}
              />
            </Col>
          )}
        </Row>
      </fieldset>
      <div className="create-record-form__actions d-flex justify-content-end gap-2 mt-2">
        {onCancel && (
          <Button variant="outline-secondary" type="button" onClick={onCancel} disabled={formik.isSubmitting}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          className="btn-quote-blue py-2 px-3 box-shedow-none outline-none"
          type="submit"
          disabled={fieldsDisabled}
        >
          {formik.isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Form>
  );
};

export const CreateIndustryForm = () => {
  const navigate = useNavigate();

  const handleSuccess = async (result) => {
    showSuccessToast(result.message)
    navigate('/industries');
  };

  return (
    <Card border="light" className="bg-white shadow-sm mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">Add Industry</h5>
          <Link to="/industries" className="text-decoration-none btn-quote-back d-inline-flex align-items-center">
            Back to Listing
          </Link>
        </div>
        <IndustryForm onSuccess={handleSuccess} />
      </Card.Body>
    </Card>
  );
};
